import { Box, Flex, VStack } from "@chakra-ui/react";
import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import InvestmentProgressV2 from "@equidefi/portals/components/investments/InvestmentProgressV2";
import { useInvestmentSignatures } from "@equidefi/portals/hooks/useAgreements";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useGetFullVaultRecords } from "@equidefi/portals/hooks/useVault";
import { offeringIsCompleted, offeringIsRule506c, offeringIsTerminated } from "@equidefi/shared";
import { BannerAlert } from "@equidefi/ui";
import type React from "react";
import { useParams } from "react-router";

import { ReinvestBox } from "@/components/investment/ReinvestBox";
import Continue from "@/components/investment/continue";
import { VAULT_ROUTES } from "@/constants/routes";
import { useInvestment } from "@/hooks/useInvestments";

import { VaultContainer } from "../components";
import {
  AccreditationStepCard,
  AgreementStepCard,
  IdentityStepCard,
  InvestmentDetailsCard,
  InvestmentTitleCard,
  OfferingDetailsCard,
  PaymentStepCard,
  VaultPageHeader,
} from "./components";

const InvestmentDetailsV2: React.FC = () => {
  useDocumentTitle(["Investment Details"]);

  const { id } = useParams<{ id: string }>();

  const { data: investment, isFetchedAfterMount, isError } = useInvestment(id, { refetchOnMount: "always" });

  const { data: signatures, isFetched: isSignaturesFetched } = useInvestmentSignatures(investment?.id);
  const { data: vaultRecords } = useGetFullVaultRecords(investment?.id);

  const issuer = investment?.issuer;
  const offering = investment?.offering;

  if (!isFetchedAfterMount || !isSignaturesFetched) {
    return null;
  }

  if (isError || !investment || !id) {
    return (
      <VaultContainer>
        <BannerAlert
          status="error"
          title="Failed to Load Investment"
          description="We couldn't load your investment details. Please try again later."
        />
      </VaultContainer>
    );
  }

  const isOfferingTerminated = offeringIsTerminated(offering);
  const canReinvest = !offeringIsCompleted(offering) && investment.status !== "CANCELED";

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment" },
        ]}
        backTo="/vault/dashboard"
        backLabel="Back To Portfolio"
      />

      <VStack align="stretch" spacing="4">
        <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />

        {canReinvest && offering && <ReinvestBox offering={offering} />}

        <Flex direction={{ base: "column", lg: "row" }} align="start" gap={{ base: 4, lg: 6 }}>
          <VStack flex={{ lg: "1" }} w="full" minW="0" align="stretch" spacing="4">
            <InvestmentProgressV2 investment={investment} offering={offering}>
              {investment.status === "PENDING" && (
                <Box mt="4">
                  <Continue investment={investment} offering={offering} />
                </Box>
              )}
            </InvestmentProgressV2>

            {offering?.investor_alert_enabled && offering?.investor_alert_message && (
              <BannerAlert
                status="warning"
                title="Notice to Investors"
                description={<EquidefiMarkdown>{offering.investor_alert_message}</EquidefiMarkdown>}
              />
            )}

            {isOfferingTerminated && (
              <BannerAlert status="info" title="Offering Has Ended" description="The offering has concluded." />
            )}

            <InvestmentDetailsCard investment={investment} offering={offering} />
          </VStack>

          <VStack flex={{ lg: "1" }} w="full" minW="0" align="stretch" spacing="4">
            <PaymentStepCard investment={investment} />
            <IdentityStepCard investment={investment} vaultRecords={vaultRecords} />
            <AgreementStepCard investment={investment} signatures={signatures} />
            {offeringIsRule506c(offering) && <AccreditationStepCard investment={investment} />}

            <OfferingDetailsCard offering={offering} issuer={issuer} />
          </VStack>
        </Flex>
      </VStack>
    </VaultContainer>
  );
};

export default InvestmentDetailsV2;
